import type { StringOverrides } from "./index";

/**
 * en-US override table. Re-declares only the keys that differ from
 * the canonical `en` table. Lookup chain falls back to `en` for any
 * key absent here, so partial coverage never blanks out the UI.
 *
 * What's overridden today:
 *   - `quote` -> `estimate` (US trades quote pre-work via "estimate";
 *     "quote" reads slightly British). Touches button labels, page
 *     headings, dialog titles, toast copy, empty states.
 *
 * What's NOT overridden:
 *   - "tradesperson" / "tradespeople" -- not in the en strings, only
 *     in inline JSX comments. Marketing copy is handled separately
 *     via the per-region painHero arrays in for-trades.ts.
 *   - VAT / Companies House / postcode -- these come from
 *     @barrykilby/settings jurisdiction lookup, not en.ts strings.
 *
 * Add new overrides as inline JSX UK-isms get hoisted into the
 * registry during the audit sweep.
 */
export const enUS: StringOverrides = {
  // ─── Tour copy ─────────────────────────────────────────────────
  tour: {
    quotes: {
      title: "Estimates",
      body: "Price up a job here. Your customer gets a link they can accept on their phone.",
    },
    quotesStart: {
      title: "Start an estimate",
      body: "Pick the customer, add what you're charging for, send it. They get a link on their phone -- you'll see when they accept.",
    },
  },

  // ─── Empty states ──────────────────────────────────────────────
  empty: {
    quotesNoneYet: "No estimates yet. Press {{action}} above to send your first one.",
    quotesInFlight: "Estimates you've drafted or sent show up here.",
  },

  // ─── Inbox section ─────────────────────────────────────────────
  // The awaiting-response empty state lives nested under
  // inbox.empty in the canonical en table; the consumer reads
  // t("inbox.empty.awaitingResponse").
  inbox: {
    empty: {
      awaitingResponse: "No estimates awaiting customer response.",
    },
  },

  // ─── Toast copy ────────────────────────────────────────────────
  toast: {
    quoteSent: "Estimate sent to {{customerName}}. We'll let you know when they open it.",
  },

  // ─── Buttons ───────────────────────────────────────────────────
  buttons: {
    newQuote: "New estimate",
    sendQuote: "Send estimate",
  },

  // ─── Dialogs ───────────────────────────────────────────────────
  dialog: {
    deleteQuote: {
      title: "Delete this estimate?",
      confirm: "Delete estimate",
    },
  },

  // ─── Quotes list page ──────────────────────────────────────────
  // truncationNotice is the canonical key in en.ts; the consumer
  // reads t("quotes.truncationNotice"). Earlier this file had the
  // same wording under "emptyBody", which doesn't exist in the en
  // type and so was silently unused.
  quotes: {
    pageTitle: "Estimates",
    newDialogTitle: "New estimate",
    emptyTitle: "No estimates in flight",
    truncationNotice:
      "Showing first 250 estimates. Some data may be truncated -- use sort or archive older jobs to narrow results.",
  },
};
